/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useEffect, useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Link, UpdateNote } from '@ta-interaktiv/contentbricks'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import ExcessDeath from 'components/Charts_ExcessDeath'
import ExcessDeathRegion from 'components/Charts_ExcessDeathRegion'
import { HeaderStandaloneRC } from 'components/HeaderStandaloneRC'
import Key from 'components/Charts_Key'
import Chart from 'components/Charts_Chart'
import { ExcessDeathRelative } from 'components/Charts_ExcessDeathRelative/component'

// TODO Set the correct article ID here
const ARTICLE_ID = '172597671179'
const isEmbeded =
  new URL(window.location.toString()).searchParams.get('emb') === '1'
const hideFlag =
  new URL(window.location.toString()).searchParams.get('hideflag') === '1'
const hideRegions =
  new URL(window.location.toString()).searchParams.get('hideregions') === '1'
const hideMain =
  new URL(window.location.toString()).searchParams.get('hidemain') === '1'
const hideRelative =
  new URL(window.location.toString()).searchParams.get('hiderelative') === '1'

const currentVersionDate = isEmbeded ? '20. Februar 2024' : '11. Januar 2022'
const nextVersionDate = '27. Februar 2024'
const chartStartDate = '2020-01-01'
const formatDate = datestring => {
  const [day, month, year] = datestring.split('.')

  return year + '-' + month + '-' + day
}

export function AppComponent() {
  const [historicalData, setHistoricalData] = useState({ old: [], young: [] })
  const [relData, setRelData] = useState({ old: [], young: [] })
  const [regionalData, setRegionalData] = useState([])

  useEffect(() => {
    fetch('regions.json')
      .then(response => {
        return response.json()
      })
      .then(data =>
        setRegionalData(
          data.map(region => {
            return {
              name: region[0].name,
              id: region[0].id,
              data: region
                .map(m => {
                  return { d: formatDate(m.date), v: m.sK, oG: m.oG, uG: m.uG }
                })
                .filter(f => f.v)
            }
          })
        )
      )
  }, [])
  useEffect(() => {
    fetch('data.json')
      .then(response => {
        return response.json()
      })
      .then(data => {
        const young = data['0-64'].map(m => ({
          d: formatDate(m._d),
          v: m.tF,
          uG: m.uG,
          oG: m.oG,
          erw: ((m.tF - m.erw) / 100) * m.erw
        }))
        const old = data['65+'].map(m => ({
          d: formatDate(m._d),
          v: m.tF,
          uG: m.uG,
          oG: m.oG,
          erw: m.erw,
          rel: ((m.tF - m.erw) * 100) / m.erw
        }))
        const youngrel = data['0-64'].map(m => ({
          d: formatDate(m._d),
          v: ((m.tF - m.erw) * 100) / m.erw,
          uG: ((m.uG - m.erw) * 100) / m.erw,
          oG: ((m.oG - m.erw) * 100) / m.erw,
          erw: m.erw,
          rel: ((m.tF - m.erw) * 100) / m.erw
        }))
        const oldrel = data['65+'].map(m => ({
          d: formatDate(m._d),
          v: ((m.tF - m.erw) * 100) / m.erw,
          uG: ((m.uG - m.erw) * 100) / m.erw,
          oG: ((m.oG - m.erw) * 100) / m.erw,
          erw: m.erw,
          rel: ((m.tF - m.erw) * 100) / m.erw
        }))

        setRelData({ old: oldrel, young: youngrel })

        setHistoricalData({ old, young })
      })
  }, [])
  return (
    <div className='index'>
      <ThemeSwitch />
      {!isEmbeded && (
        <>
          <Masthead articleId={ARTICLE_ID} />
          <HeaderStandaloneRC updated={<>{currentVersionDate}</>} />

          <div className='ui vertical very fitted segment'>
            <div className='ui text container'>
              <p>
                In vergangenen Jahr sind in der Schweiz fast 2500 mehr Menschen
                gestorben als erwartet. Das zeigt unsere Auswertung der{' '}
                <Link
                  allowWordWrap
                  href='https://www.bfs.admin.ch/bfs/de/home/statistiken/gesundheit/gesundheitszustand/sterblichkeit-todesursachen.html'
                >
                  Todesfallzahlen des Bundesamtes für Statistik (BFS)
                </Link>
                . Anhand der Fallzahlen der vorangegangenen zehn Jahre berechnet
                das BFS jeweils einen Erwartungswert für jede einzelne
                Kalenderwoche. Wird dieser deutlich überschritten, spricht man
                von einer Übersterblichkeit.
              </p>
              <p>
                Aktuell ist das gerade wieder der Fall. Seit Mitte November gibt
                es bei über 65-Jährigen mehr Todesfälle, als in dieser Zeit
                üblicherweise auftreten. Es ist bereits die dritte Sterbewelle
                in dieser Altersgruppe seit dem Ausbruch der Corona-Pandemie.
                Schon während der ersten und der zweiten Welle kam es zu einer
                Übersterblichkeit, die noch höher war als in diesem Winter.
              </p>
            </div>
          </div>
        </>
      )}

      {isEmbeded && !hideFlag && (
        <UpdateNote
          updating='Wird dienstags aktualisiert'
          current={'Stand: ' + currentVersionDate}
        />
      )}
      {!hideMain && (
        <>
          <div className={isEmbeded ? '' : 'ui container'}>
            <Chart
              style={{ marginTop: isEmbeded ? 0 : undefined }}
              title='Wöchentliche Todesfälle in der Schweiz'
              sub={
                <>
                  Die Statistik des BFS zeigt die Todesfälle unabhängig von der
                  Todesursache in der Schweiz immer mit einer Verzögerung von
                  gut einer Woche.
                  <br /> Aktualisiert am {currentVersionDate}. Die nächste
                  Aktualisierung findet am {nextVersionDate} statt.
                </>
              }
              source={
                <>Quelle: Bundesamt für Statistik – Todesursachenstatistik</>
              }
            >
              <Key
                entries={[
                  {
                    type: 'line',
                    label: 'Sterblichkeit',
                    color: '#7D7D7D'
                  },
                  {
                    type: 'line',
                    label: 'Über- bzw. Untersterblichkeit',
                    color: '#D3343F'
                  },
                  {
                    type: 'shape',
                    label:
                      'obere und untere Grenze des statistisch zu erwartenden Wertes',
                    color: '#D4D4D4'
                  }
                ]}
              />

              {historicalData.old.length > 0 && (
                <ExcessDeath
                  data={[
                    historicalData.young.filter(
                      f => new Date(f.d) >= new Date(chartStartDate)
                    ),
                    historicalData.old.filter(
                      f => new Date(f.d) >= new Date(chartStartDate)
                    )
                  ]}
                  xDomain={[
                    new Date(chartStartDate),
                    new Date(
                      new Date().getFullYear(),
                      window.innerWidth > 600 ? new Date().getMonth() + 5 : 15,
                      0
                    )
                  ]}
                  yDomain={[2000, 0]}
                  height={window.innerWidth > 600 ? 500 : 400}
                  yTicks={{
                    text: [500, 1000, 1500, 2000],
                    lines: [0, 500, 1000, 1500, 2000]
                  }}
                  xTicks={{
                    text: [
                      '2020-01-01',
                      '2021-01-01',
                      '2022-01-01',
                      '2023-01-01'
                    ],
                    lines: [
                      '2020-01-01',
                      '2021-01-01',
                      '2022-01-01',
                      '2023-01-01'
                    ]
                  }}
                />
              )}
            </Chart>
          </div>
        </>
      )}

      {!hideRelative && (
        <div className={isEmbeded ? 'ui text container' : 'ui text container'}>
          <Chart
            style={{ marginTop: isEmbeded ? 0 : undefined }}
            title='Abweichung der wöchentlichen Todesfälle zur Norm'
            sub={<>in % zum statistisch erwartbaren Wert, Altersgruppe 65+</>}
            source={
              <>Quelle: Bundesamt für Statistik – Todesursachenstatistik</>
            }
          >
            <Key
              entries={[
                {
                  type: 'line',
                  label: 'Sterblichkeit',
                  color: '#7D7D7D'
                },
                {
                  type: 'line',
                  label: 'Über- bzw. Untersterblichkeit',
                  color: '#D3343F'
                },
                {
                  type: 'shape',
                  label:
                    'obere und untere Grenze des statistisch zu erwartenden Wertes',
                  color: '#D4D4D4'
                }
              ]}
            />
            {relData.old.length > 0 && (
              <ExcessDeathRelative
                data={[
                  relData.old.filter(
                    f => new Date(f.d) >= new Date(chartStartDate)
                  )
                ]}
                xDomain={[
                  new Date(chartStartDate),
                  new Date(
                    new Date().getFullYear(),
                    window.innerWidth > 600 ? new Date().getMonth() + 5 : 11,
                    0
                  )
                ]}
                unit='%'
                yDomain={[100, -50]}
                height={window.innerWidth > 600 ? 500 : 400}
                yTicks={{
                  text: [50, 100],
                  lines: [-50, 0, 50, 100]
                }}
                xTicks={{
                  text: [
                    '2020-01-01',
                    '2021-01-01',
                    '2022-01-01',
                    '2023-01-01'
                  ],
                  lines: [
                    '2020-01-01',
                    '2021-01-01',
                    '2022-01-01',
                    '2023-01-01'
                  ]
                }}
              />
            )}
          </Chart>
        </div>
      )}
      {!isEmbeded && (
        <>
          <div className='ui text container box' style={{ marginTop: '2rem' }}>
            <h4>So berechnet das BFS die Übersterblichkeit</h4>
            <p>
              Jeder Todesfall in der Schweiz muss beim regional zuständigen
              Zivilstandsamt gemeldet werden. Die Daten werden dem BFS
              übermittelt, das die «beobachteten» Zahlen mit den «erwarteten»
              Zahlen vergleicht. Es berechnet Erwartungswerte anhand der
              Fallzahlen der vorangegangenen zehn Jahre. Um eine Verzerrung
              wegen der Corona-Pandemie zu umgehen, beruht die Berechnung der
              Übersterblichkeit 2021 weiterhin auf der für das Jahr 2020
              erwarteten Zahl der Todesfälle.
            </p>
            <p>
              Die Berechnung entspricht nicht einfach einem Durchschnittswert,
              sondern berücksichtigt die Veränderung der Bevölkerung von Jahr zu
              Jahr. Für jeden Erwartungswert wird eine Bandbreite berechnet,
              innerhalb derer Schwankungen als zufällig gewertet werden. Wird
              diese überschritten, ist die Abweichung also nicht mehr mit
              zufälligen Schwankungen erklärbar, spricht man von einer
              Übersterblichkeit.
            </p>
          </div>
          <div className='ui text container'>
            <p>
              Dass die Übersterblichkeit im vergangenen Jahr weniger stark
              ausfiel als noch 2020, hat verschiedene Gründe. Ein entscheidender
              Faktor ist sicher die Covid-Impfung, die viele schwere Verläufe
              und Todesfälle verhindert. Während Ende 2020 bis zu 1900 Menschen
              pro Woche starben, waren es auf dem Höhepunkt der aktuellen
              Übersterblichkeit am 26. Dezember noch 1600. Das
              Gesundheitspersonal hat inzwischen auch mehr Erfahrung in der
              Behandlung von Covid-Patientinnen und -Patienten.
            </p>
            <p>
              Zudem gab es zwischen Ende Februar und Mitte April eine
              Untersterblichkeit, das Pendel schlug also auf die andere Seite
              aus. Das hat zum einen mit Statistik zu tun: Weil einige Personen,
              die im Zuge der zweiten Welle starben, ohnehin nur noch wenige
              Wochen zu leben gehabt hätten, «fehlten» diese Todesfälle in den
              nachfolgenden Monaten. Zum anderen spielt das Ausbleiben der
              Grippewelle eine wichtige Rolle: Weil die Mobilität und die
              Kontakte reduziert wurden und vielerorts Maskenpflicht galt, gab
              es im vergangenen Winter kaum Fälle. Die Grippe fordert
              normalerweise mehrere Hundert, in schweren Saisons sogar über 2000
              Todesfälle, die ausblieben. Auch andere Infektionskrankheiten sind
              vermutlich infolge der Hygienemassnahmen zurückgegangen.
            </p>
            <p>
              Trotz all diesen Einschränkungen gab es im vergangenen Jahr zu
              viele Todesfälle. Es starben 69’679 Menschen – 1890 oder 2,8
              Prozent mehr als 2019, dem letzten normalen Jahr vor der Pandemie.
              Zum Vergleich: Seither wuchs die Schweizer Bevölkerung nur um 0,9
              Prozent.
            </p>
            <AutofitIFrame src='https://datawrapper.dwcdn.net/riazv/' />
            {/* <DeathsPerYear data={deathperyearData} /> */}
            <p>
              Ohne Massnahmen wäre die Differenz zu einem gewöhnlichen Jahr noch
              grösser ausgefallen. Denn 2021 starben über 4200 Menschen an den
              Folgen einer Covid-Erkrankung, wie Zahlen des{' '}
              <Link
                allowWordWrap
                href='https://www.covid19.admin.ch/de/epidemiologic/death?rel=abs&sum=cumulative&time=total'
              >
                Bundesamtes für Gesundheit (BAG)
              </Link>{' '}
              zeigen. Hätte es also keinen Rückgang bei den Todesfällen aufgrund
              anderer Ursachen gegeben, sähe die Bilanz weitaus schlechter aus.
            </p>
            <p>
              Nicht alle Regionen sind indes gleichermassen betroffen. In Zürich
              zum Beispiel gab es während der aktuellen Corona-Welle nur für
              eine kurze Zeit mehr Todesfälle als üblich. Das Tessin
              verzeichnete gar keine Übersterblichkeit. In der Ost- und der
              Zentralschweiz hingegen hielt diese wochenlang an. Die
              Genferseeregion war 2020 am stärksten gebeutelt, als zeitweise
              mehr als doppelt so viele Menschen starben wie erwartet. 2021
              zeigte sich im Westen des Landes ein ganz anderes Bild.
            </p>
          </div>
        </>
      )}
      <>
        <div className='ui vertical segment'>
          {!hideRegions && (
            <div className={!isEmbeded ? 'ui container ' : ''}>
              <Chart
                title={
                  <>
                    Wöchentliche Todesfälle nach Grossregion, 65-Jährige und
                    ältere
                  </>
                }
                source={
                  <>
                    {' '}
                    Quelle:{' '}
                    <a
                      target={isEmbeded ? '_blank' : '_self'}
                      href='https://www.experimental.bfs.admin.ch/expstat/de/home/innovative-methoden/momo.html'
                    >
                      Bundesamt für Statistik
                    </a>
                  </>
                }
              >
                <Key
                  entries={[
                    {
                      type: 'line',
                      label: 'Sterblichkeit',
                      color: '#7D7D7D'
                    },
                    {
                      type: 'line',
                      label: 'Über- bzw. Untersterblichkeit',
                      color: '#D3343F'
                    },
                    {
                      type: 'shape',
                      label:
                        'obere und untere Grenze des statistisch zu erwartenden Wertes',
                      color: '#D4D4D4'
                    }
                  ]}
                />
                <div className='charts'>
                  {regionalData.length > 0 &&
                    regionalData.map((region, i) => {
                      return (
                        <div className='chartwrapper'>
                          <Chart title={region.name}>
                            <ExcessDeathRegion
                              data={[region.data]}
                              height={200}
                              xDomain={[new Date('2020-01-01'), new Date()]}
                              yDomain={[600, 0]}
                              yTicks={{
                                text: [200, 400, 600],
                                lines: [0, 200, 400, 600]
                              }}
                              xTicks={{
                                text: ['2020-01-01', '2021-01-01'],
                                lines: [
                                  '2020-01-01',
                                  '2021-01-01',
                                  '2022-01-01',
                                  '2023-01-01'
                                ]
                              }}
                              className={'region_' + i}
                            />
                          </Chart>
                        </div>
                      )
                    })}
                </div>
              </Chart>
            </div>
          )}
          {!isEmbeded && (
            <>
              <div className='ui text container'>
                <p>
                  Auch in früheren Jahren ist es immer wieder mal zu einer
                  Übersterblichkeit gekommen. Etwa 2003, als die Schweiz den
                  heissesten Sommer seit Beginn ihrer Aufzeichnungen erlebte.{' '}
                  <Link
                    allowWordWrap
                    href='https://www.bag.admin.ch/bag/de/home/gesund-leben/umwelt-und-gesundheit/hitze.html'
                  >
                    Laut dem BAG
                  </Link>{' '}
                  gab es damals zwischen Juni und August 975 zusätzliche
                  Todesfälle. Oder 2015, als wegen einer sehr starken
                  Grippewelle über das ganze Jahr gesehen 2200 Personen mehr
                  starben als erwartet. Doch das steht in keinem Verhältnis zu
                  den Auswirkungen der Corona-Pandemie, die in weniger als zwei
                  Jahren schon mehr als 12’000 Opfer gefordert hat.
                </p>
              </div>
            </>
          )}
        </div>
        {!isEmbeded && (
          <Footer
            articleId={ARTICLE_ID}
            questions={[
              'Wie informativ war der Beitrag?',
              'Wie verständlich ist die Grafik?'
            ]}
            credits={[
              [
                'Konzept, Recherche, Umsetzung',
                'Marc Brupbacher, Yannick Wiget, Andreas Frei, Sebastian Broschinski, Patrick Vögeli'
              ],
              [
                'Daten',
                '<a href="https://www.bfs.admin.ch/bfs/de/home/statistiken/gesundheit/gesundheitszustand/sterblichkeit-todesursachen.html">Bundesamt für Statistik – Todesursachenstatistik</a>'
              ]
            ]}
          />
        )}
      </>
    </div>
  )
}

// const deathperyearData = require('./gender-deathsperyear.csv')

// const lifeExpData = require('./gender-life-exp.csv')
// const DeathsPerYear = ({ data }) => {
//   return (
//     <>
//       <div className='title'>Deutlich mehr Todesfälle im Jahr 2020</div>
//       <div className='sub nomarginbottom'>
//         Anzahl der verstorbenen Personen pro Jahr
//       </div>
//       <div className='keywrapper'>
//         <div className='key'>
//           <div className='entry'>
//             <img className='icon' src={require('images/ueb_key-03.svg')} />
//             <div className='label'>Frauen</div>
//           </div>
//           <div className='entry'>
//             <img className='icon' src={require('images/ueb_key-04.svg')} />
//             <div className='label'>Männer </div>
//           </div>

//           <div className='entry'>
//             <img className='icon' src={require('images/ueb_key-05.svg')} />
//             <div className='label'>Wohnbevölkerung</div>
//           </div>
//           <div className='entry nL'>
//             {' '}
//             <div className='label'>Veränderung in Prozent</div>
//           </div>
//         </div>
//       </div>
//       <EasyChart
//         heights={[200, 300]}
//         data={[
//           { className: 'horizontalTicks', type: 'hticks', values: [0] },
//           {
//             className: 'horizontalTicks',
//             type: 'hticks',
//             values: [-100, -5, 5, 10, 15]
//           },
//           {
//             className: 'yearticks',
//             type: 'vticks',
//             values: [
//               [9, '1980'],
//               [19, '1990'],
//               [29, '2000'],
//               [39, '2010'],
//               [49, '2020']
//             ]
//           },
//           {
//             type: 'curve',
//             values: data.map(m => m.men),
//             className: 'men'
//           },
//           {
//             type: 'curve',
//             values: data.map(m => m.women),
//             className: 'women'
//           },
//           {
//             type: 'curve',
//             values: data.map(m => m.pop),
//             className: 'pop'
//           }
//         ]}
//         y={[-5, 15]}
//       />
//       <div className='source'>
//         Quelle:{' '}
//         <a
//           target={isEmbeded ? '_blank' : '_self'}
//           href='https://www.experimental.bfs.admin.ch/expstat/de/home/innovative-methoden/momo.html'
//         >
//           Bundesamt für Statistik
//         </a>{' '}
//       </div>
//     </>
//   )
// }
// const LifeExpectancy = ({ data }) => {
//   return (
//     <>
//       <div className='title'>Veringerte Lebenserwartung</div>
//       {/* <div className='sub nomarginbottom'>
//         Veränderung der Lebenserwartung bei Geburt
//       </div> */}
//       <div className='keywrapper'>
//         <div className='key'>
//           <div className='entry'>
//             <img className='icon' src={require('images/ueb_key-03.svg')} />
//             <div className='label'>Frauen</div>
//           </div>
//           <div className='entry'>
//             <img className='icon' src={require('images/ueb_key-04.svg')} />
//             <div className='label'>Männer </div>
//           </div>

//           <div className='entry nL'>
//             {' '}
//             <div className='label'>
//               Veränderung der Lebenserwartung bei Geburt in Prozent
//             </div>
//           </div>
//         </div>
//       </div>
//       <EasyChart
//         heights={[200, 300]}
//         data={[
//           { className: 'horizontalTicks', type: 'hticks', values: [0] },
//           {
//             className: 'horizontalTicks',
//             type: 'hticks',
//             values: [-10, -1, -0.5, 0.5, 1, 1.5, 2]
//           },
//           {
//             className: 'yearticks',
//             type: 'vticks',
//             values: [
//               [4, '2005'],
//               [9, '2010'],
//               [14, '2015'],
//               [19, '2020']
//             ]
//           },
//           {
//             type: 'curve',
//             values: data.map(m => m.men),
//             className: 'men'
//           },
//           {
//             type: 'curve',
//             values: data.map(m => m.women),
//             className: 'women'
//           }
//         ]}
//         y={[-1, 2]}
//       />{' '}
//       <div className='source'>
//         Quelle:{' '}
//         <a
//           target={isEmbeded ? '_blank' : '_self'}
//           href='https://www.experimental.bfs.admin.ch/expstat/de/home/innovative-methoden/momo.html'
//         >
//           Bundesamt für Statistik
//         </a>{' '}
//       </div>
//     </>
//   )
// }
